import React, { useState, useEffect } from "react";
import { Container, Form } from "react-bootstrap";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { goBack } from "../../../utils/otherHelopers";
import { routes } from "../../../routes/pageRoutes";
import { useDispatch, useSelector } from "react-redux";
import {
    CalendarIcon,
    SmartPhoneIcon,
    WebsiteIcon,
    ArrowLeftIcon,
    MessageAddIcon,
    UserIcon04,
    LocationIcon01,
} from "../../../assets/icons";
import "../../../modules/Users/AllUsers/index.css";
import CommonButton from "../../../components/commonButton";

import CommonVerifyModal from "../../../components/verify-modal/VerifyModal";
import { fetchVerificationDetails, updateVerificationStatus } from "../../../redux/slice/companySlice";
import { useToast } from "../../../context/TostContext";
import { usePermission } from "../../../hooks/usePermission";
import { createPortal } from "react-dom";
import StatusChip from "../../../components/statusChip/StatusChip";
import {
    Verified,
    IdCard,
    FileText,
    FolderOpen,
    Wallet,
    User,
    Phone,
    Mail,
    Globe,
    MapPin,
    Tag,
    Eye,
    ChevronUp,
    ChevronDown,
    CheckCircle2,
} from "lucide-react";
import "./index.css"
import { TabCard } from "../../../components/custom-card/Cards";
import CustomTable from "../../../components/table/CustomTable";
import RejectDocumentModal from "../../../components/reject-modal/RejectDocumentModal";

const MODULE = 'company_verification';

// Map doc card ID -> the status key sent to backend
const DOC_STATUS_KEY_MAP = {
    pan: "pan_status",
    business: "business_status",
    other: "other_documents_status",
};


const renderHeaderContent = (handleBack) => (<>
    <button className="bg-transparent border-0 primary" onClick={handleBack}>
        <ArrowLeftIcon size={20} />
    </button>
    <h5 className="fw-semibold mb-0 fs-lg">
        Verification Company Details
    </h5></>

);
const RequestDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const dispatch = useDispatch();
    const { verificationDetails, updateVerificationLoading } = useSelector(state => state.company);
    const { showToast } = useToast();
    const { hasPermission } = usePermission();

    const [verifyModal, setVerifyModal] = useState({ show: false, doc: null });
    const [rejectModal, setRejectModal] = useState({ show: false, docId: null });
    const [isPaymentExpanded, setIsPaymentExpanded] = useState(true);
    const [panDecision, setPanDecision] = useState("");
    const [businessDecision, setBusinessDecision] = useState("");
    const [otherDecision, setOtherDecision] = useState("");

    useEffect(() => {
        if (verificationDetails) {
            setPanDecision(verificationDetails.pan_status || "");
            setBusinessDecision(verificationDetails.business_status || "");
            setOtherDecision(verificationDetails.other_documents_status || "");
        }
    }, [verificationDetails]);


    const statusCards = [
        {
            id: 1,
            title: "PAN Status",
            status: "Submitted",
            icon: <IdCard size={20} />,
            bgColor: "var(--blue-100)",
            iconColor: "var(--blue)",
            onClick: () => console.log("PAN clicked"),
        },
        {
            id: 2,
            title: "GST Status",
            status: "Submitted",
            icon: <FileText size={20} />,
            bgColor: "var(--success-100)",
            iconColor: "var(--success)",
            onClick: () => console.log("GST clicked"),
        },
        {
            id: 3,
            title: "Documents Status",
            status: "Submitted",
            icon: <FolderOpen size={20} />,
            bgColor: "var(--primary-100)",
            iconColor: "var(--primary)",
            onClick: () => console.log("Documents clicked"),
        },
        {
            id: 4,
            title: "Payment Status",
            status: "Paid",
            icon: <Wallet size={20} />,
            bgColor: "var(--warning-100)",
            iconColor: "var(--warning)",
            onClick: () => console.log("Payment clicked"),
        },
    ];
    useEffect(() => {
        if (id) {
            dispatch(fetchVerificationDetails(id));
        }
    }, [dispatch, id]);

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        return date.toLocaleString("en-GB", {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
        });
    };

    const getPaymentMethodText = (payment) => {
        if (!payment) return "-";
        const method = payment.payment_method;
        if (!method) return "Online";
        if (typeof method === 'string') return method;
        if (typeof method === 'object') {
            if (method.card) {
                const card = method.card;
                const network = card.card_network || card.channel || "Card";
                const last4 = card.card_number ? card.card_number.slice(-4) : "";
                return last4 ? `${network} •••• ${last4}` : network;
            }
            if (method.upi) return "UPI";
            if (method.netbanking) return method.netbanking.bank_name || "Netbanking";
            if (method.wallet) return method.wallet.wallet_name || "Wallet";
        }
        return payment.payment_group?.toUpperCase() || "Online";
    };

    const handleDecisionSubmit = () => {
        if (!panDecision || !businessDecision || (verificationDetails?.other_documents_files?.length > 0 && !otherDecision)) {
            showToast("Please select a decision (Approve or Reject) for all required documents before submitting.", "danger");
            return;
        }

        const payload = {
            id,
            pan_status: panDecision,
            business_status: businessDecision,
            other_documents_status: otherDecision || 'pending',
        };

        dispatch(updateVerificationStatus(payload))
            .unwrap()
            .then(() => {
                showToast("Verification decisions submitted successfully", "success");
                dispatch(fetchVerificationDetails(id));
            })
            .catch((err) => {
                showToast(err?.message || "Failed to submit verification decisions", "danger");
            });
    };

    const company = verificationDetails?.company || {};
    const creator = company?.creator || {};
    const profile = company?.profile || {};

    const companyInfo = {
        name: company.name || "N/A",
        category: company.category?.name || "N/A",
        date: formatDate(company.created_at),
        logo: company.profile?.logo?.url || "/images/AllUser/home.png",
        basicDetails: [
            { icon: <UserIcon04 width={18} height={18} color="var(--primary)" fillOpacity={0.12} />, label: "Person Name", value: creator ? `${creator.first_name} ${creator.last_name || ""}`.trim() : "N/A" },
            { icon: <SmartPhoneIcon size={18} />, label: "Phone", value: profile.mobile_number || "N/A" },
            { icon: <MessageAddIcon />, label: "Email", value: profile.email || "N/A" },
            { icon: <WebsiteIcon className="primary" />, label: "Website", value: profile.website || "N/A" },
            { icon: <LocationIcon01 width={18} height={18} />, label: "Address", value: profile.address || "N/A" },
        ]
    };

    const documents = [
        {
            id: 'pan',
            title: "Company PAN Card Document",
            status: verificationDetails?.pan_status || "pending",
            docNumber: verificationDetails?.pan_number || "N/A",
            issueDate: formatDate(verificationDetails?.created_at),
            proofUrls: verificationDetails?.pancard_file?.url ? [verificationDetails.pancard_file.url] : []
        },
        {
            id: 'business',
            title: `Company ${verificationDetails?.business_type?.toUpperCase() || "Business"} Document`,
            status: verificationDetails?.business_status || "pending",
            docNumber: verificationDetails?.business_number || "N/A",
            issueDate: formatDate(verificationDetails?.created_at),
            proofUrls: verificationDetails?.business_document_file?.url ? [verificationDetails.business_document_file.url] : []
        }
    ];

    if (verificationDetails?.other_documents_files?.length > 0) {
        documents.push({
            id: 'other',
            title: "Other Documents",
            status: verificationDetails?.other_documents_status || "pending",
            docNumber: "N/A",
            issueDate: formatDate(verificationDetails?.created_at),
            proofUrls: verificationDetails.other_documents_files.map(file => file.url)
        });
    }

    const isDocSettled = (status) => status === 'approved' || status === 'rejected';

    // --- Individual handlers ---
    const handleDocApprove = (docId) => {
        const statusKey = DOC_STATUS_KEY_MAP[docId];
        dispatch(updateVerificationStatus({ id, [statusKey]: 'approved' }))
            .unwrap()
            .then(() => {
                showToast("Document approved successfully", "success");
                dispatch(fetchVerificationDetails(id));
            })
            .catch((err) => {
                showToast(err?.message || "Failed to approve document", "danger");
            });
    };

    const handleDocReject = (docId) => {
        setRejectModal({ show: true, docId });
    };

    const handleRejectSubmit = (reason) => {
        const statusKey = DOC_STATUS_KEY_MAP[rejectModal.docId];
        dispatch(updateVerificationStatus({ id, [statusKey]: 'rejected' }))
            .unwrap()
            .then(() => {
                showToast("Document rejected successfully", "success");
                setRejectModal({ show: false, docId: null });
                dispatch(fetchVerificationDetails(id));
            })
            .catch((err) => {
                showToast(err?.message || "Failed to reject document", "danger");
            });
    };

    // --- Global handlers ---


    const handleRejectAllSubmit = (reason) => {
        dispatch(updateVerificationStatus({
            id,
            pan_status: 'rejected',
            business_status: 'rejected',
            other_documents_status: 'rejected',
        }))
            .unwrap()
            .then(() => {
                showToast("All documents rejected", "success");
                setRejectModal({ show: false, docId: null });
                dispatch(fetchVerificationDetails(id));
            })
            .catch((err) => {
                showToast(err?.message || "Failed to reject all documents", "danger");
            });
    };

    const onRejectModalSubmit = (reason) => {
        if (rejectModal.docId === 'all') {
            handleRejectAllSubmit(reason);
        } else {
            handleRejectSubmit(reason);
        }
    };

    const handleBack = () => goBack(navigate, location, routes.verificationRequests);
    const headerPortalTarget = document.getElementById("header-portal-target");
    return (<>
        {headerPortalTarget && createPortal(
            renderHeaderContent(handleBack),
            headerPortalTarget
        )}
        <Container fluid className="px-0 request-detail">
            <div className="company-header-card grid mb-3">
                <div className="g-col-8 d-flex gap-4">
                    <div className="company-logo-wrapper">
                        <img src={companyInfo?.logo || "/logo-main.png"} alt="logo" className="w-100 h-auto object-fit-cover" />
                    </div>

                    <div>
                        <h4
                            className="company-name cursor-pointer"
                            onClick={() => company?.id && navigate(routes.viewCompany.replace(":id", company.id))}
                        >
                            {companyInfo?.name}
                        </h4>
                        {companyInfo?.category && (
                            <p className="company-category">{companyInfo.category}</p>
                        )}
                        <div className="d-flex gap-2">
                            {creator?.mobile_verified && (<StatusChip bg="success" title={<><Verified size={14} className="me-1 text-successs" /> mobile verified</>} />)}
                            {creator?.email_verified && (<StatusChip bg="success" title={<><Verified size={14} className="me-1 text-successs" /> email verified</>} />)}
                        </div>
                    </div>
                </div>
                <div className="g-col-4">
                    <div className="d-flex gap-3 align-items-center mb-2">
                        <CalendarIcon />
                        <div><span className="meta-label">Submitted on
                        </span>
                            <p className="meta-value">{companyInfo?.date}</p></div>
                    </div>

                    <div className="meta-row mb-2">
                        <div className="meta-icon-wrapper text-secondary">
                            <Verified width={20} height={20} />
                        </div>
                        <div className="meta-content inline">
                            <span className="meta-label">Overall Status</span>
                            <StatusChip
                                bg={verificationDetails?.overall_status === "approved" ? "success" : verificationDetails?.overall_status === "rejected" ? "danger" : "warning"}
                                title={verificationDetails?.overall_status || "submitted"}
                                className="text-capitalize"
                            />
                        </div>
                    </div>

                    <div className="meta-row mb-2">
                        <div className="meta-icon-wrapper">
                            <CalendarIcon width={20} height={20} color="#718096" />
                        </div>
                        <div className="meta-content inline">
                            <span className="meta-label">Payment Status</span>
                            <StatusChip
                                bg={verificationDetails?.payment_status === "paid" ? "success" : "warning"}
                                title={verificationDetails?.payment_status || "paid"}
                                className="text-capitalize"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className="grid mb-3">
                {statusCards?.map((status, idx) => (
                    <TabCard
                        icon={status.icon}
                        title={status?.title}
                        count={status?.status}
                        key={idx}
                        iconColor={status?.iconColor}
                        iconBg={status?.bgColor}
                        onClick={status?.onClick}
                        className="g-col-3"
                    />
                ))}
            </div>
            <div className="grid gap-3">
                <div className="g-col-5 info-card">
                    <h5 className="mb-2 pb-2 text-primary border-bottom fw-bold fs-normal">Company Contact Information</h5>
                    <div className="info-table">
                        <div className="info-row">
                            <div className="info-label-cell">
                                <User size={16} className="info-icon" />
                                <span>Contact Person</span>
                            </div>
                            <div className="info-value-cell">
                                {creator ? `${creator.first_name} ${creator.last_name || ""}`.trim() : "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <Phone size={16} className="info-icon" />
                                <span>Mobile Number</span>
                            </div>
                            <div className="info-value-cell">
                                {profile?.mobile_number || "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <Mail size={16} className="info-icon" />
                                <span>Email Address</span>
                            </div>
                            <div className="info-value-cell">
                                {profile?.email || "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <Globe size={16} className="info-icon" />
                                <span>Website</span>
                            </div>
                            <div className="info-value-cell">
                                {profile?.website || "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <MapPin size={16} className="info-icon" />
                                <span>Address</span>
                            </div>
                            <div className="info-value-cell">
                                {profile?.address || "N/A"}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="g-col-4 info-card">
                    <h5 className="mb-2 pb-2 text-primary border-bottom fw-bold fs-normal">Verification Information</h5>
                    <div className="info-table">
                        <div className="info-row">
                            <div className="info-label-cell">
                                <Tag size={16} className="info-icon" />
                                <span>Verification Type</span>
                            </div>
                            <div className="info-value-cell">
                                {verificationDetails?.business_type?.toUpperCase() || "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <IdCard size={16} className="info-icon" />
                                <span>PAN Number</span>
                            </div>
                            <div className="info-value-cell">
                                {verificationDetails?.pan_number ? (
                                    <span className="info-value-tag">{verificationDetails.pan_number}</span>
                                ) : "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <FileText size={16} className="info-icon" />
                                <span>GSTIN Number</span>
                            </div>
                            <div className="info-value-cell">
                                {verificationDetails?.business_number ? (
                                    <span className="info-value-tag">{verificationDetails.business_number}</span>
                                ) : "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <User size={16} className="info-icon" />
                                <span>Submitted By</span>
                            </div>
                            <div className="info-value-cell">
                                {creator ? `${creator.first_name} ${creator.last_name || ""}`.trim() : "N/A"}
                            </div>
                        </div>
                        <div className="info-row">
                            <div className="info-label-cell">
                                <CalendarIcon width={16} height={16} className="info-icon" />
                                <span>Submitted On</span>
                            </div>
                            <div className="info-value-cell">
                                {formatDate(verificationDetails?.created_at)}
                            </div>
                        </div>
                    </div>
                </div>
                <div className="g-col-3 verification-decision-card">
                    <div className="d-flex align-items-center justify-content-between m-0 mb-2">
                        <h5 className="mb-2 pb-2 text-primary border-bottom fw-bold fs-normal">Verification Decision</h5>
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-link p-0 text-success text-decoration-none"
                                style={{ fontSize: "12px", fontWeight: "500" }}
                                onClick={() => {
                                    setPanDecision("approved");
                                    setBusinessDecision("approved");
                                    if (verificationDetails?.other_documents_files?.length > 0) {
                                        setOtherDecision("approved");
                                    }
                                }}
                            >
                                Approve All
                            </button>
                            <span className="text-muted" style={{ fontSize: "12px" }}>|</span>
                            <button
                                type="button"
                                className="btn btn-link p-0 text-danger text-decoration-none"
                                style={{ fontSize: "12px", fontWeight: "500" }}
                                onClick={() => {
                                    setPanDecision("rejected");
                                    setBusinessDecision("rejected");
                                    if (verificationDetails?.other_documents_files?.length > 0) {
                                        setOtherDecision("rejected");
                                    }
                                }}
                            >
                                Reject All
                            </button>
                        </div>
                    </div>

                    {/* PAN Card Decision */}
                    <div className="decision-section">
                        <h6 className="decision-title">PAN Card</h6>
                        <div className="decision-options">
                            <label className="decision-radio-label">
                                <Form.Check
                                    type="radio"
                                    name="panDecision"
                                    value="approved"
                                    checked={panDecision === "approved"}
                                    onChange={(e) => setPanDecision(e.target.value)}
                                />
                                <span>Approve</span>
                            </label>
                            <label className="decision-radio-label">
                                <Form.Check
                                    type="radio"
                                    name="panDecision"
                                    value="rejected"
                                    checked={panDecision === "rejected"}
                                    onChange={(e) => setPanDecision(e.target.value)}
                                />
                                <span>Reject</span>
                            </label>
                        </div>
                    </div>

                    {/* GST/Business Decision */}
                    <div className="decision-section">
                        <h6 className="decision-title">{verificationDetails?.business_type?.toUpperCase() || "GST"} Certificate</h6>
                        <div className="decision-options">
                            <label className="decision-radio-label">
                                <Form.Check
                                    type="radio"
                                    name="businessDecision"
                                    value="approved"
                                    checked={businessDecision === "approved"}
                                    onChange={(e) => setBusinessDecision(e.target.value)}

                                />
                                <span>Approve</span>
                            </label>
                            <label className="decision-radio-label">
                                <Form.Check
                                    type="radio"
                                    name="businessDecision"
                                    value="rejected"
                                    checked={businessDecision === "rejected"}
                                    onChange={(e) => setBusinessDecision(e.target.value)}

                                />
                                <span>Reject</span>
                            </label>
                        </div>
                    </div>

                    {/* Other Documents Decision */}
                    {verificationDetails?.other_documents_files?.length > 0 && (
                        <div className="decision-section">
                            <h6 className="decision-title">Other Documents</h6>
                            <div className="decision-options">
                                <label className="decision-radio-label">
                                    <Form.Check
                                        type="radio"
                                        name="otherDecision"
                                        value="approved"
                                        checked={otherDecision === "approved"}
                                        onChange={(e) => setOtherDecision(e.target.value)}

                                    />
                                    <span>Approve</span>
                                </label>
                                <label className="decision-radio-label">
                                    <Form.Check
                                        type="radio"
                                        name="otherDecision"
                                        value="rejected"
                                        checked={otherDecision === "rejected"}
                                        onChange={(e) => setOtherDecision(e.target.value)}

                                    />
                                    <span>Reject</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="d-flex flex-column gap-2 mt-3">
                        <CommonButton
                            title="Submit Decisions"
                            variant="primary"
                            onClick={handleDecisionSubmit}
                            disabled={updateVerificationLoading}
                            icon={<CheckCircle2 size={18} className="me-1" />}
                        />
                    </div>
                </div>
                <div className="g-col-9">
                    {/* Document Review Section */}
                    <h5 className="section-title">Document Review</h5>
                    <div className="document-review-grid">
                        {/* PAN Card Document Card */}
                        {(() => {
                            const doc = documents.find(d => d.id === 'pan');
                            if (!doc) return null;
                            return (
                                <div className="document-card">
                                    <div className="document-card-header">
                                        <h6 className="document-card-title">PAN Card</h6>
                                        <StatusChip
                                            bg={doc.status === "approved" ? "success" : doc.status === "rejected" ? "danger" : "warning"}
                                            title={doc.status}

                                            className="text-capitalize"
                                        />
                                    </div>
                                    <div className="document-preview-wrapper cursor-pointer" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: doc.proofUrls[0] } })}>
                                        {doc.proofUrls[0] ? (
                                            <img src={doc.proofUrls[0]} alt="PAN Card" className="document-preview-image" />
                                        ) : (
                                            <div className="h-100 d-flex align-items-center justify-content-center text-muted small">No Preview Available</div>
                                        )}
                                    </div>
                                    <div className="document-card-footer">
                                        <span className="document-card-label">PAN Number</span>
                                        <span className="document-card-value">{doc.docNumber}</span>
                                    </div>
                                    <button className="document-action-btn" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: doc.proofUrls[0] } })}>
                                        <Eye size={16} />
                                        View Full Size
                                    </button>
                                </div>
                            );
                        })()}

                        {/* GST/Business Document Card */}
                        {(() => {
                            const doc = documents.find(d => d.id === 'business');
                            if (!doc) return null;
                            return (
                                <div className="document-card">
                                    <div className="document-card-header">
                                        <h6 className="document-card-title">{verificationDetails?.business_type?.toUpperCase() || "GST"} Certificate</h6>
                                        <StatusChip
                                            bg={doc.status === "approved" ? "success" : doc.status === "rejected" ? "danger" : "warning"}
                                            title={doc.status}
                                            pill
                                            className="text-capitalize"
                                        />
                                    </div>
                                    <div className="document-preview-wrapper cursor-pointer" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: doc.proofUrls[0] } })}>
                                        {doc.proofUrls[0] ? (
                                            <img src={doc.proofUrls[0]} alt="GST Certificate" className="document-preview-image" />
                                        ) : (
                                            <div className="h-100 d-flex align-items-center justify-content-center text-muted small">No Preview Available</div>
                                        )}
                                    </div>
                                    <div className="document-card-footer">
                                        <span className="document-card-label">{verificationDetails?.business_type?.toUpperCase() || "GSTIN"} Number</span>
                                        <span className="document-card-value">{doc.docNumber}</span>
                                    </div>
                                    <button className="document-action-btn" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: doc.proofUrls[0] } })}>
                                        <Eye size={16} />
                                        View Full Size
                                    </button>
                                </div>
                            );
                        })()}

                        {/* Other Documents Card */}
                        {(() => {
                            const doc = documents.find(d => d.id === 'other');
                            if (!doc) return (
                                <div className="document-card d-flex align-items-center justify-content-center" style={{ minHeight: "280px" }}>
                                    <FolderOpen size={32} className="text-muted mb-2" />
                                    <p className="m-0 text-muted small">No other documents uploaded</p>
                                </div>
                            );
                            return (
                                <div className="document-card">
                                    <div className="document-card-header">
                                        <h6 className="document-card-title">Other Documents ({doc.proofUrls?.length || 0})</h6>
                                        <StatusChip
                                            bg={doc.status === "approved" ? "success" : doc.status === "rejected" ? "danger" : "warning"}
                                            title={doc.status}
                                            pill
                                            className="text-capitalize"
                                        />
                                    </div>
                                    <div className="document-multi-previews">
                                        {doc.proofUrls?.map((url, idx) => (
                                            <div key={idx} className="document-thumbnail" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: url } })}>
                                                <img src={url} alt={`other-${idx}`} />
                                            </div>
                                        ))}
                                    </div>
                                    <div className="document-card-footer">
                                        <span className="document-card-label">{doc.proofUrls?.length || 0} Documents Uploaded</span>
                                    </div>
                                    <button className="document-action-btn" onClick={() => setVerifyModal({ show: true, doc: { ...doc, proofUrl: doc.proofUrls[0] } })}>
                                        <FolderOpen size={16} />
                                        View All
                                    </button>
                                </div>
                            );
                        })()}
                    </div>

                    {/* Payment History Card (Toggleable) */}
                    <div className="payment-history-card">
                        <div className="payment-history-header" onClick={() => setIsPaymentExpanded(!isPaymentExpanded)}>
                            <h5 className="payment-history-title">Payment History ({verificationDetails?.payments?.length || 0} Transactions)</h5>
                            <div className="payment-history-toggle-icon">
                                {isPaymentExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                            </div>
                        </div>

                        {isPaymentExpanded && (() => {
                            const paymentColumns = [
                                {
                                    header: "Date & Time",
                                    cell: (_, payment) => formatDate(payment.paid_at || payment.created_at)
                                },
                                {
                                    header: "Order ID",
                                    cell: (_, payment) => payment.cf_payment_id || payment.cashfree_order_id || "-"
                                },
                                {
                                    header: "Amount",
                                    cell: (_, payment) => `₹${payment.amount}`
                                },
                                {
                                    header: "Status",
                                    cell: (_, payment) => (
                                        <StatusChip
                                            pill
                                            bg={payment.status === "paid" ? "success" : "danger"}
                                            title={payment.status || "Failed"}
                                            className="text-capitalize"
                                        />
                                    )
                                },
                                {
                                    header: "Payment Method",
                                    cell: (_, payment) => getPaymentMethodText(payment)
                                }
                            ];
                            return (
                                <div className="payment-table-container">
                                    <CustomTable
                                        disabledAction
                                        columns={paymentColumns}
                                        data={verificationDetails?.payments || []}
                                        noDataMessage="No payment transactions found."
                                    />
                                </div>
                            );
                        })()}
                    </div>
                </div>



                <CommonVerifyModal
                    isOpen={verifyModal.show}
                    onClose={() => setVerifyModal({ show: false, doc: null })}
                    title="Verify Document"
                    fields={[
                        { label: "Document Number", value: verifyModal.doc?.docNumber, disabled: true },
                        { label: "Issue Date", value: verifyModal.doc?.issueDate, disabled: true }
                    ]}
                    imageSrc={verifyModal.doc?.proofUrl || "/assets/images/aadhar-card.png"}
                    showApprove={hasPermission(MODULE, 'verify') && !isDocSettled(verifyModal.doc?.status)}
                    showReject={hasPermission(MODULE, 'verify') && !isDocSettled(verifyModal.doc?.status)}
                    onApprove={() => {
                        if (verifyModal.doc?.id) {
                            handleDocApprove(verifyModal.doc.id);
                            setVerifyModal({ show: false, doc: null });
                        }
                    }}
                    onReject={() => {
                        if (verifyModal.doc?.id) {
                            handleDocReject(verifyModal.doc.id);
                            setVerifyModal({ show: false, doc: null });
                        }
                    }}
                    size={verifyModal.doc?.proofUrl?.toLowerCase().endsWith(".pdf") ? "xl" : "lg"}
                    className="verify-modal"
                />

                <RejectDocumentModal
                    isOpen={rejectModal.show}
                    onClose={() => setRejectModal({ show: false, docId: null })}
                    onReject={onRejectModalSubmit}
                    title={rejectModal.docId === 'all' ? "Reject All Documents" : "Reject Document"}
                    className="reject-document-modal"
                />
            </div>


        </Container>
    </>

    );
};

export default RequestDetail;
